// agentsSlice.ts
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { Agents, AgentStats } from "../types/master";
import axiosClient from "../api/AxiosClient";

// ---- User Preferences Types ----
export interface AgentRating {
  agent_name: string;
  thumbs_up: number;
  thumbs_down: number;
  thumbs_up_percentage: number;
  thumbs_down_percentage: number;
}

export interface UserPreferences {
  All_agents_rating: AgentRating[];
  total_thumbs_up_percentage: number;
  total_thumbs_down_percentage: number;
}

// ---- Token Consumption Types ----
export interface TokenConsumptionItem {
  date: string;
  fullDate: string;
  tokens: number;
  total_token_consumption: number;
}

export interface TokenConsumptionResponse {
  token_consumption: TokenConsumptionItem[];
}

// ---- Async Thunk to fetch agents ----
export const fetchAgents = createAsyncThunk(
  "agents/fetchAgents",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get<Agents[]>("/agents/");
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || err.message || "Failed to fetch agents",
      );
    }
  },
);

// ---- Async Thunk to fetch a specific agent by ID ----
export const fetchAgentById = createAsyncThunk(
  "agents/fetchAgentById",
  async (agentId: string | number, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get<Agents>(`/agents/${agentId}`);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || err.message || "Failed to fetch agent",
      );
    }
  },
);

// ---- Async Thunk to fetch agent by identifier (ID or framework_name) ----
export const fetchAgentByIdentifier = createAsyncThunk(
  "agents/fetchAgentByIdentifier",
  async (identifier: string, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState() as { agents: AgentsState };
      let agents = state.agents.data;

      // Fetch agents list if not already loaded
      if (agents.length === 0) {
        agents = await dispatch(fetchAgents()).unwrap();
      }

      // Find agent by either numeric ID or framework_name
      const numericId = Number(identifier);
      const isNumeric =
        !isNaN(numericId) && numericId.toString() === identifier;

      const agent = agents.find((a) =>
        isNumeric ? a.id === numericId : a.agent_framework_name === identifier,
      );

      if (!agent) {
        return rejectWithValue(
          `Agent with identifier "${identifier}" not found`,
        );
      }

      // Return the agent from the list (no additional API call needed)
      return agent;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || err.message || "Failed to fetch agent",
      );
    }
  },
);

// ---- Async Thunk to fetch agent statistics ----
export const fetchAgentStats = createAsyncThunk(
  "agents/fetchAgentStats",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get<AgentStats>(
        "/agents/stats/user-count",
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || err.message || "Failed to fetch agent statistics",
      );
    }
  },
);

// ---- Async Thunk to fetch user preferences ----
export const fetchUserPreferences = createAsyncThunk(
  "agents/fetchUserPreferences",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get<UserPreferences>(
        "/agents_user_preferences",
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || err.message || "Failed to fetch user preferences",
      );
    }
  },
);

// ---- Async Thunk to fetch token consumption ----
export const fetchTokenConsumption = createAsyncThunk(
  "agents/fetchTokenConsumption",
  async (
    { month, year, type }: { month: number; year: number; type: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosClient.get<TokenConsumptionResponse>(
        `/get_token_consumption?month=${month}&year=${year}&type=${type}`
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || err.message || "Failed to fetch token consumption"
      );
    }
  }
);

// ---- Async Thunk to send terms and conditions acceptance ----
export const sendTermsAcceptance = createAsyncThunk(
  "agents/sendTermsAcceptance",
  async (userId: number, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post("/user_agent_access_mapping", {
        mappings: [
          {
            user_id: userId,
            agent_id: 501,
            is_access: 1,
          },
        ],
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || err.message || "Failed to send acceptance"
      );
    }
  }
);

// ---- Async Thunk to record agent actions (download, etc.) ----
export const recordAgentAction = createAsyncThunk(
  "agents/recordAgentAction",
  async (
    {
      agentId = 11,
      actionType,
      content,
    }: {
      agentId?: number;
      actionType: string;
      content: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosClient.post("/agent_action", {
        agent_id: agentId,
        action_type: actionType,
        content,
      });
      return response.data;
    } catch (err: any) {
      // Silently fail - don't block user action
      console.error("Failed to record agent action:", err);
      return rejectWithValue(
        err.response?.data || err.message || "Failed to record agent action"
      );
    }
  }
);

interface AgentsState {
  data: Agents[];
  loading: boolean;
  error: string | null;
  currentAgent: Agents | undefined;
  currentAgentLoading: boolean;
  currentAgentError: string | null;
  statsData: AgentStats | null;
  statsLoading: boolean;
  statsError: string | null;
  userPreferences: UserPreferences | null;
  userPreferencesLoading: boolean;
  userPreferencesError: string | null;
  tokenConsumption: TokenConsumptionItem[];
  tokenConsumptionLoading: boolean;
  tokenConsumptionError: string | null;
  termsAcceptanceLoading: boolean;
  termsAcceptanceError: string | null;
}

const initialState: AgentsState = {
  data: [],
  loading: false,
  error: null,
  currentAgent: undefined,
  currentAgentLoading: false,
  currentAgentError: null,
  statsData: null,
  statsLoading: false,
  statsError: null,
  userPreferences: null,
  userPreferencesLoading: false,
  userPreferencesError: null,
  tokenConsumption: [],
  tokenConsumptionLoading: false,
  tokenConsumptionError: null,
  termsAcceptanceLoading: false,
  termsAcceptanceError: null,
};

const agentsSlice = createSlice({
  name: "agents",
  initialState,
  reducers: {
    updateAgentAcceptance(state, action: { payload: number }) {
      const agent = state.data.find((a) => a.id === action.payload);
      if (agent) {
        agent.is_access = 1;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAgents.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAgents.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        // Persist user_id to localStorage for admin check
        if (action.payload.length > 0 && action.payload[0].user_id !== undefined) {
          localStorage.setItem('user_id', action.payload[0].user_id.toString());
        }
      })
      .addCase(fetchAgents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch agents";
      })
      .addCase(fetchAgentById.pending, (state) => {
        state.currentAgentLoading = true;
        state.currentAgentError = null;
      })
      .addCase(fetchAgentById.fulfilled, (state, action) => {
        state.currentAgentLoading = false;
        state.currentAgent = action.payload;
      })
      .addCase(fetchAgentById.rejected, (state, action) => {
        state.currentAgentLoading = false;
        state.currentAgentError =
          action.error.message || "Failed to fetch agent";
      })
      .addCase(fetchAgentByIdentifier.pending, (state) => {
        state.currentAgentLoading = true;
        state.currentAgentError = null;
      })
      .addCase(fetchAgentByIdentifier.fulfilled, (state, action) => {
        state.currentAgentLoading = false;
        state.currentAgent = action.payload;
      })
      .addCase(fetchAgentByIdentifier.rejected, (state, action) => {
        state.currentAgentLoading = false;
        state.currentAgentError =
          action.error.message || "Failed to fetch agent";
      })
      .addCase(fetchAgentStats.pending, (state) => {
        state.statsLoading = true;
        state.statsError = null;
      })
      .addCase(fetchAgentStats.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.statsData = action.payload;
      })
      .addCase(fetchAgentStats.rejected, (state, action) => {
        state.statsLoading = false;
        state.statsError =
          action.error.message || "Failed to fetch agent statistics";
      })
      .addCase(fetchUserPreferences.pending, (state) => {
        state.userPreferencesLoading = true;
        state.userPreferencesError = null;
      })
      .addCase(fetchUserPreferences.fulfilled, (state, action) => {
        state.userPreferencesLoading = false;
        state.userPreferences = action.payload;
      })
      .addCase(fetchUserPreferences.rejected, (state, action) => {
        state.userPreferencesLoading = false;
        state.userPreferencesError =
          action.error.message || "Failed to fetch user preferences";
      })
      .addCase(fetchTokenConsumption.pending, (state) => {
        state.tokenConsumptionLoading = true;
        state.tokenConsumptionError = null;
      })
      .addCase(fetchTokenConsumption.fulfilled, (state, action) => {
        state.tokenConsumptionLoading = false;
        state.tokenConsumption = action.payload.token_consumption;
      })
      .addCase(fetchTokenConsumption.rejected, (state, action) => {
        state.tokenConsumptionLoading = false;
        state.tokenConsumptionError =
          (action.payload as any)?.detail ||
          action.error.message ||
          "Failed to fetch token consumption";
      })
      .addCase(sendTermsAcceptance.pending, (state) => {
        state.termsAcceptanceLoading = true;
        state.termsAcceptanceError = null;
      })
      .addCase(sendTermsAcceptance.fulfilled, (state) => {
        state.termsAcceptanceLoading = false;
      })
      .addCase(sendTermsAcceptance.rejected, (state, action) => {
        state.termsAcceptanceLoading = false;
        state.termsAcceptanceError =
          action.error.message || "Failed to send acceptance";
      });
  },
});

export const { updateAgentAcceptance } = agentsSlice.actions;
export default agentsSlice.reducer;
